import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import * as yup from "yup";

import BaseErrors from "@components/fields/BaseErrors";
import CheckboxGroup from "@components/fields/CheckboxGroup";
import EmployerSelect from "@components/fields/EmployerSelect";
import Input from "@components/fields/Input";
import Select from "@components/fields/Select";
import Form from "@components/forms/Form";
import { employerSelectSelfPaid } from "@typing/Constants";
import {
  AutomationConditionKindEnum,
  AutomationConditionUpdateDocument,
  AutomationConditionUpdateMutation,
  AutomationConditionUpdateMutationVariables,
  AutomationFragment as AutomationType,
  CareProfileActionableFieldEnum,
  CareProfileActionableFieldValueEnum,
  DurationUnitsEnum
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";
import {
  optionsForAutomationConditionKind,
  optionsForCareProfileActionableField,
  optionsForCareProfileActionableFieldValue,
  optionsForDurationUnits
} from "@utils/optionUtils";

type AutomationConditionType = Extract<
  AutomationType["blocks"][number],
  { __typename: "AutomationConditionBlock" }
>["automationConditionGroups"][number]["automationConditions"][number];

type Props = {
  automationCondition: AutomationConditionType;
  locked: boolean;
};

type FormData = {
  careProfileField: CareProfileActionableFieldEnum | null;
  careProfileValues: CareProfileActionableFieldValueEnum[];
  duration: number | null;
  durationUnits: DurationUnitsEnum | null;
  employerId: string | null;
  kind: AutomationConditionKindEnum | null;
};

const AutomationConditionForm = ({ automationCondition, locked }: Props) => {
  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const defaultValues: FormData = {
    careProfileField: automationCondition?.careProfileField ?? null,
    careProfileValues: automationCondition?.careProfileValues ?? [],
    duration: automationCondition.duration,
    durationUnits: automationCondition.durationUnits,
    employerId: automationCondition?.employer?.id ?? null,
    kind: automationCondition?.kind ?? null
  };

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      careProfileField: yup
        .string()
        .defined()
        .oneOf(Object.values(CareProfileActionableFieldEnum))
        .when("kind", ([kind], schema) => {
          if (kind === AutomationConditionKindEnum.CARE_PROFILE_VALUE_IS) {
            return schema.required(intl.formatMessage({ id: "errors.automationCondition.careProfileField.blank" }));
          }
          return schema.nullable();
        }),
      careProfileValues: yup
        .array()
        .defined()
        .of(yup.string().oneOf(Object.values(CareProfileActionableFieldValueEnum)).required())
        .when("kind", ([kind], schema) => {
          if (kind === AutomationConditionKindEnum.CARE_PROFILE_VALUE_IS) {
            return schema.min(1, intl.formatMessage({ id: "errors.automationCondition.careProfileValues.blank" }));
          }
          return schema.of(yup.string()).nullable();
        }),
      duration: yup.number().defined().nullable(),
      durationUnits: yup.string().defined().nullable().oneOf(Object.values(DurationUnitsEnum)),
      employerId: yup.string().nullable().defined(),
      kind: yup
        .string()
        .oneOf(Object.values(AutomationConditionKindEnum))
        .required(intl.formatMessage({ id: "errors.automationCondition.kind.blank" }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const [updateAutomationCondition] = useMutation<
    AutomationConditionUpdateMutation,
    AutomationConditionUpdateMutationVariables
  >(AutomationConditionUpdateDocument);

  const careProfileField = form.watch("careProfileField");
  const careProfileValues = form.watch("careProfileValues");
  const duration = form.watch("duration");
  const durationUnits = form.watch("durationUnits");
  const employerId = form.watch("employerId");
  const kind = form.watch("kind");

  useEffect(() => {
    if (kind === AutomationConditionKindEnum.CARE_PLAN_IS_PUBLISHED) {
      form.setValue("careProfileValues", []);
      form.setValue("careProfileField", null);
    }
  }, [form, kind]);

  useEffect(() => {
    setBaseErrors([]);

    let useEmployerId = employerId;
    if (useEmployerId === employerSelectSelfPaid) {
      useEmployerId = null;
    }

    updateAutomationCondition({
      variables: {
        careProfileField,
        careProfileValues,
        duration: duration ? parseInt(duration.toString()) : null,
        durationUnits,
        employerId: useEmployerId,
        id: automationCondition.id,
        kind
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.automationConditionUpdate,
        intl,
        onError: () => {},
        onSuccess: () => {},
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.automationCondition"
      });
    });
  }, [
    automationCondition.id,
    careProfileField,
    careProfileValues,
    duration,
    durationUnits,
    employerId,
    form.setError,
    intl,
    kind,
    updateAutomationCondition
  ]);

  const kindOptions = optionsForAutomationConditionKind(intl);
  const careProfileFieldOptions = optionsForCareProfileActionableField(intl);
  const careProfileValueOptions = optionsForCareProfileActionableFieldValue(careProfileField, intl);
  const durationUnitsOptions = useMemo(() => optionsForDurationUnits(intl), [intl]);

  return (
    <Form hookForm={form} onSubmit={() => {}}>
      <BaseErrors errors={baseErrors} />
      <div>
        <Select<FormData>
          control={form.control}
          disabled={locked}
          error={form.formState.errors.kind}
          label={intl.formatMessage({ id: "forms.automationCondition.kindLabel" })}
          name="kind"
          options={kindOptions}
        />
      </div>
      <div className={kind === AutomationConditionKindEnum.CARE_PROFILE_VALUE_IS ? undefined : "ion-hide"}>
        <Select<FormData>
          control={form.control}
          disabled={locked}
          error={form.formState.errors.careProfileField}
          label={intl.formatMessage({ id: "forms.automationCondition.careProfileFieldLabel" })}
          name="careProfileField"
          options={careProfileFieldOptions}
        />
        <CheckboxGroup<FormData>
          control={form.control}
          disabled={locked}
          errors={form.formState.errors.careProfileValues}
          label={intl.formatMessage({ id: "forms.automationCondition.careProfileValuesLabel" })}
          name="careProfileValues"
          options={careProfileValueOptions ?? []}
        />
      </div>
      <div
        className={
          kind &&
          [
            AutomationConditionKindEnum.MEMBER_MESSAGE_SINCE,
            AutomationConditionKindEnum.TIME_SINCE_LAST_CONSULT
          ].includes(kind)
            ? undefined
            : "ion-hide"
        }
      >
        <Input<FormData>
          control={form.control}
          disabled={locked}
          error={form.formState.errors.duration}
          label={intl.formatMessage({ id: "forms.automationCondition.durationLabel" })}
          name="duration"
          required
          type="number"
        />
        <Select<FormData>
          control={form.control}
          disabled={locked}
          error={form.formState.errors.durationUnits}
          label={intl.formatMessage({ id: "forms.automationCondition.durationUnitsLabel" })}
          name="durationUnits"
          options={durationUnitsOptions}
          required
        />
      </div>
      <div className={kind === AutomationConditionKindEnum.OWNER_EMPLOYER_IS ? undefined : "ion-hide"}>
        <EmployerSelect<FormData>
          allowAny={false}
          allowSelfPaid={true}
          control={form.control}
          error={form.formState.errors.employerId}
          initialOption={{
            label: automationCondition.employer?.name ?? intl.formatMessage({ id: "dictionary.selfPaid" }),
            value: automationCondition.employer?.id ?? null
          }}
          label={intl.formatMessage({ id: "forms.automationCondition.employerIdLabel" })}
          name="employerId"
        />
      </div>
    </Form>
  );
};

export default AutomationConditionForm;
